import { ChangeDetectionStrategy, Component, inject, input, OnInit, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { StorageService } from '@core/storage/services/storage.service';
import {
  BannerComponent,
  BannerType,
  InputComponent,
  LargeButtonComponent,
  LargeIconButtonComponent,
  LinkComponent,
  PrefetchLinkDirective,
} from '@kaseya-design-components';

import { TranslatePipe } from '@ngx-translate/core';
import { LoginRecoveryPath } from '@pages/login-recovery-options/routes/login-recovery-path.enum';
import { ApplicationPath } from '@core/navigation/models/application-path.enum';
import { MobileAppLinksComponent } from '@pages/login/components/mobile-app-links/mobile-app-links.component';
import { CompanyLoginSsoFacade } from '@pages/login/facades/company-login-sso.facade';
import { LogInWithThirdPartySsoFacade } from '@pages/login/facades/log-in-with-third-party-sso.facade';
import { LoginFacade } from '@pages/login/facades/login.facade';
import { ConfigurationFormGroup } from '@pages/login/models/login-configuration/configuration-form-group.interface';
import { CurrentLoginFlowService } from '@core/sso/services/current-login-flow.service';
import { asNonUndefined } from '@kaseya-utils';
import { helpdeskContactUrl } from '@shared/constants/helpdesk-urls';

@Component({
  selector: 'itc-login-configuration',
  templateUrl: 'login-configuration.component.html',
  styleUrl: 'login-configuration.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputComponent,
    LargeButtonComponent,
    RouterLink,
    PrefetchLinkDirective,
    MobileAppLinksComponent,
    BannerComponent,
    LargeIconButtonComponent,
    TranslatePipe,
    LinkComponent,
  ],
})
export class LoginConfigurationComponent implements OnInit {
  private readonly loginFacade = inject(LoginFacade);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly companyLoginSsoFacade = inject(CompanyLoginSsoFacade);
  private readonly logInWithThirdPartySsoFacade = inject(LogInWithThirdPartySsoFacade);
  private readonly router = inject(Router);
  private readonly storageService = inject(StorageService);
  private readonly currentLoginWorkflowService = inject(CurrentLoginFlowService);

  readonly isBookmarkTip = signal(!this.storageService.getIsBookmarkTipViewed());
  readonly loginConfigurationForm = input.required<FormGroup<ConfigurationFormGroup>>();
  readonly organizationName = input.required<string | undefined>();
  readonly uuid = input.required<string | undefined>();
  readonly isSsoEnabled = this.companyLoginSsoFacade.isSsoEnabled;
  readonly samlLoginUrl = this.companyLoginSsoFacade.samlLoginUrl;
  readonly loginConfigurationLoading = this.loginFacade.loginConfigurationLoading;
  readonly errorMessageTranslationKey = this.loginFacade.errorMessageTranslationKey;
  readonly isZendeskLoginFlow = this.currentLoginWorkflowService.isZendeskLoginFlow;
  readonly BannerType = BannerType;
  readonly ApplicationPath = ApplicationPath;
  readonly LoginRecoveryPath = LoginRecoveryPath;
  readonly helpdeskLink = helpdeskContactUrl;

  ngOnInit(): void {
    const organizationName = this.organizationName();
    const uuid = this.uuid();
    if (organizationName && uuid) {
      this.companyLoginSsoFacade.loadSsoStatus(organizationName, uuid);
    }
  }

  verifyLoginConfiguration(): void {
    const loginConfigurationForm = this.loginConfigurationForm();

    if (loginConfigurationForm.invalid) {
      this.markAllAsDirty();
      return;
    }

    const currentUrl = location.href;
    const returnToUrl = currentUrl.endsWith('/login') ? location.origin : currentUrl;

    this.loginFacade.loadLoginConfiguration({
      username: asNonUndefined(loginConfigurationForm.value.username),
      organizationName: asNonUndefined(loginConfigurationForm.value.organizationName),
      returnToUrl,
    });
  }

  logInWithThirdPartySSO(): void {
    this.logInWithThirdPartySsoFacade.logInWithThirdPartySso(asNonUndefined(this.samlLoginUrl()));
  }

  closeBookmarkTip(): void {
    this.isBookmarkTip.set(false);
    this.storageService.setIsBookmarkTipToViewed();
  }

  clearOrganizationName(): void {
    this.clearQueryParams();
    this.loginFacade.goToLoginConfigurationStep();
  }

  private clearQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {},
    });
  }

  private markAllAsDirty(): void {
    const loginConfigurationForm = this.loginConfigurationForm();

    Object.values(loginConfigurationForm.controls).forEach(control => {
      control.markAsDirty();
      control.updateValueAndValidity();
    });
  }
}
